import React, { useEffect, useState } from "react";
import { Download, CalendarDays, PlayCircle, Clock, Receipt, LogIn, Loader2, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Section, SectionHeading } from "@/components/blocks";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "";

function Empty({ text, to, cta, testId }) {
  return (
    <Card className="p-8 text-center border-dashed border-slate-300 bg-slate-50" data-testid={testId}>
      <p className="text-slate-600">{text}</p>
      <Button asChild variant="outline" className="mt-4 rounded-full"><Link to={to}>{cta}</Link></Button>
    </Card>
  );
}

export default function Account() {
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/account").then((r) => setData(r.data)).catch(() => setErr(true));
  }, [user]);

  if (loading) return <div className="py-32 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>;

  if (!user) {
    return (
      <Section>
        <Card className="max-w-lg mx-auto p-10 text-center border-slate-200" data-testid="account-signin-prompt">
          <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4"><LogIn className="w-7 h-7 text-teal-600" /></div>
          <h1 className="font-display text-3xl font-bold text-slate-900">My Account</h1>
          <p className="text-slate-600 mt-3">Sign in with Google to see your purchased guides, workshop bookings and recordings. Use the same email you paid with and we'll link everything automatically.</p>
          <Button onClick={login} className="mt-6 bg-teal-600 hover:bg-teal-700 rounded-full px-6" data-testid="account-signin-btn">Sign in with Google</Button>
        </Card>
      </Section>
    );
  }

  return (
    <div data-testid="account-page">
      <div className="bg-slate-900 text-white">
        <Section className="!py-14">
          <div className="flex items-center gap-4">
            {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-teal-400" /> : <div className="w-14 h-14 rounded-full bg-teal-600" />}
            <div>
              <p className="text-teal-300 text-xs font-semibold tracking-widest uppercase">My Account</p>
              <h1 className="font-display text-3xl font-bold" data-testid="account-name">{user.name}</h1>
              <p className="text-slate-400 text-sm" data-testid="account-email">{user.email}</p>
            </div>
          </div>
        </Section>
      </div>

      <Section>
        {err && <p className="text-red-600 mb-6">Could not load your account. Please refresh.</p>}
        {!data && !err && <Loader2 className="w-6 h-6 animate-spin text-teal-600" />}
        {data && (
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-14">
              <div>
                <SectionHeading eyebrow="Digital guides" title="Your downloads" />
                {data.guides.length === 0 ? <Empty text="No guides purchased yet." to="/resources" cta="Browse the resource store" testId="account-guides-empty" /> : (
                  <div className="grid sm:grid-cols-2 gap-4" data-testid="account-guides">
                    {data.guides.map((g) => (
                      <Card key={g.item_id} className="p-5 border-slate-200 flex flex-col" data-testid={`account-guide-${g.item_id}`}>
                        <h3 className="font-display font-bold text-slate-900">{g.title}</h3>
                        <p className="text-xs text-slate-500 mt-1 flex-1">Purchased {fmt(g.purchased_at)}</p>
                        <Button asChild className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid={`account-download-${g.item_id}`}>
                          <a href={g.download_url} target="_blank" rel="noreferrer"><Download className="w-4 h-4" /> Download PDF</a>
                        </Button>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <SectionHeading eyebrow="Workshops" title="Your bookings & recordings" />
                {data.workshops.length === 0 ? <Empty text="No workshops booked yet." to="/international-teachers#workshops" cta="See the workshop program" testId="account-workshops-empty" /> : (
                  <div className="space-y-3" data-testid="account-workshops">
                    {data.workshops.map((w) => (
                      <Card key={w.id} className="p-5 border-slate-200 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`account-workshop-${w.id}`}>
                        <div className="flex-1">
                          <h3 className="font-display font-bold text-slate-900">{w.title}</h3>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {fmt(w.date)}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {w.time}</span>
                            <span>{w.mode}</span>
                          </div>
                        </div>
                        {w.recording_url ? (
                          <Button asChild className="rounded-full bg-slate-900 hover:bg-teal-700" data-testid={`account-watch-${w.id}`}>
                            <a href={w.recording_url} target="_blank" rel="noreferrer"><PlayCircle className="w-4 h-4" /> Watch recording</a>
                          </Button>
                        ) : (
                          <Badge variant="outline" className="text-slate-500 border-slate-300 py-1.5 px-3" data-testid={`account-recording-soon-${w.id}`}>Recording coming soon</Badge>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <aside>
              <SectionHeading eyebrow="Receipts" title="Order history" />
              {data.orders.length === 0 ? <p className="text-sm text-slate-500" data-testid="account-orders-empty">No orders yet.</p> : (
                <ul className="space-y-3" data-testid="account-orders">
                  {data.orders.map((o) => (
                    <li key={o.id} className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white" data-testid={`account-order-${o.id}`}>
                      <Receipt className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{o.item_name}</p>
                        <p className="text-xs text-slate-500">{fmt(o.captured_at)} · Order {o.paypal_order_id}</p>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">${o.amount}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 flex items-start gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" /> Purchases made with {user.email} before you signed in are linked here automatically.</div>
            </aside>
          </div>
        )}
      </Section>
    </div>
  );
}
